"use client";

import useLandingAppointmentModal from "@/Areas/Landing/Pages/LandingAppointment/useLandingAppointmentModal";
import { Html, SmartImage, SmartLink } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useServerRequestContext, useT } from "@/Hooks";
import { FooterSectionProps } from "@/Templates/SectionsProps";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import _ from "lodash";
import {
  FC,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
} from "react";
import HomeEighteenFooterSectionGenerated, {
  HomeEighteenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenFooterSection.generated";
import HomeEightFooterSectionGenerated, {
  HomeEightFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeEightFooterSection.generated";
import HomeElevenFooterSectionGenerated, {
  HomeElevenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenFooterSection.generated";
import HomeFifteenFooterSectionGenerated, {
  HomeFifteenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenFooterSection.generated";
import HomeFiveFooterSectionGenerated, {
  HomeFiveFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveFooterSection.generated";
import HomeFourFooterSectionGenerated, {
  HomeFourFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeFourFooterSection.generated";
import HomeFourteenFooterSectionGenerated, {
  HomeFourteenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenFooterSection.generated";
import HomeNineFooterSectionGenerated, {
  HomeNineFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeNineFooterSection.generated";
import HomeOneFooterSectionGenerated, {
  HomeOneFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeOneFooterSection.generated";
import HomeSevenFooterSectionGenerated, {
  HomeSevenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenFooterSection.generated";
import HomeSeventeenFooterSectionGenerated, {
  HomeSeventeenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenFooterSection.generated";
import HomeSixFooterSectionGenerated, {
  HomeSixFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeSixFooterSection.generated";
import HomeSixteenFooterSectionGenerated, {
  HomeSixteenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenFooterSection.generated";
import HomeTenFooterSectionGenerated, {
  HomeTenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeTenFooterSection.generated";
import HomeThirteenFooterSectionGenerated, {
  HomeThirteenFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenFooterSection.generated";
import HomeThreeFooterSectionGenerated, {
  HomeThreeFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeFooterSection.generated";
import HomeTwelveFooterSectionGenerated, {
  HomeTwelveFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveFooterSection.generated";
import HomeTwoFooterSectionGenerated, {
  HomeTwoFooterSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoFooterSection.generated";
import FooterNewsletterForm from "./FooterNewsletterForm";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type FooterTranslate = ReturnType<typeof useT>;
type LandingAppointmentModal = ReturnType<typeof useLandingAppointmentModal>;
type GeneratedFooterRenderer = (
  props: FooterSectionProps,
  t: FooterTranslate,
  landingAppointmentModal: LandingAppointmentModal,
  serverRequestContext: ReturnType<typeof useServerRequestContext>,
) => ReactElement;
type GeneratedFooterProps = Record<string, unknown>;
type GeneratedFooterItem = Record<string, unknown>;
type FooterCommonDataSocialNetwork =
  FooterSectionProps["pageData"]["CommonData"]["SocialNetworks"][number];

const FooterAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateFooterAppearance;

function buildGeneratedFooterProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function getFooterMenus(props: FooterSectionProps) {
  return props.pageData.Menus.filter(
    (menu) =>
      _.intersection(menu.CenterSiteTemplateMenuLocationTypeKeys, [
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
          .key,
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
          .UsefulLinkMenu.key,
      ]).length,
  );
}

function getFooterMenusByLocation(
  props: FooterSectionProps,
  locationKey: ChildrenOf<"CenterSiteTemplateMenuLocationType">,
) {
  return props.pageData.Menus.filter((menu) =>
    menu.CenterSiteTemplateMenuLocationTypeKeys.includes(locationKey),
  );
}

function getFooterMenuLinks(props: FooterSectionProps) {
  return getFooterMenus(props).flatMap((menu) => menu.Items);
}

function getFooterSettingLinks(props: FooterSectionProps) {
  return props.lists.Links ?? [];
}

function getFooterSettingSlogans(props: FooterSectionProps) {
  return props.lists.Slogans ?? [];
}

function getFooterSettingLinkPair(props: FooterSectionProps) {
  const links = getFooterSettingLinks(props);
  return {
    first: links[0],
    second: links[1],
  };
}

function getFooterName(props: FooterSectionProps): string {
  return props.pageData.CommonData.Name ?? "Doccure";
}

function getFooterAnimatedName(props: FooterSectionProps): string {
  const name = getFooterName(props).trim();
  return name.split(/\s+/).at(-1) ?? name;
}

function getFooterDescription(props: FooterSectionProps): string {
  return props.pageData.CommonData.BriefDescription ?? "";
}

function getFooterLogoUrl(props: FooterSectionProps): string {
  return props.pageData.CommonData.Image?.Url ?? "";
}

function getFooterLogoAlt(props: FooterSectionProps): string {
  return props.pageData.CommonData.Image?.AltText ?? getFooterName(props);
}

function getFooterEmail(props: FooterSectionProps): string {
  return props.pageData.CommonData.Email ?? "";
}

function getFooterPrimaryPhone(props: FooterSectionProps): string {
  return props.pageData.CommonData.Tels[0] ?? "";
}

function getFooterSocialNetworks(props: FooterSectionProps) {
  return props.pageData.CommonData.SocialNetworks;
}

function getFooterAddress(props: FooterSectionProps): string {
  return props.pageData.CommonData.Address ?? "";
}

function getFooterTrustSealHtml(props: FooterSectionProps): string {
  return props.setting.TrustSealHtml ?? "";
}

function getFooterCopyright(props: FooterSectionProps, t: FooterTranslate): string {
  return t(
    "landing.footer.copyright",
    { year: dayjs().year(), name: getFooterName(props) },
    "© {year} All rights for this site belong to {name}.",
  );
}

function getFooterCopyrightForHome11(
  props: FooterSectionProps,
  t: FooterTranslate,
  serverRequestContext: ReturnType<typeof useServerRequestContext>,
): ReactNode {
  if (
    serverRequestContext.CenterTypeKey === CenterOptions.Enum.CenterType.Normal.key &&
    serverRequestContext.FranchiseSiteDomain
  ) {
    const domainToken = "__CLINIC24_DOMAIN__";
    const text = t(
      "landing.footer.builtWithFranchiseDomain",
      { domain: domainToken },
      "Built with {domain}",
    );
    const [prefix, suffix = ""] = text.split(domainToken);
    const domain = serverRequestContext.FranchiseSiteDomain;
    const href = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;

    return (
      <>
        {prefix}
        <SmartLink href={href}>{domain}</SmartLink>
        {suffix}
      </>
    );
  }

  return getFooterCopyright(props, t);
}

function getFooterCopyrightLead(props: FooterSectionProps): string {
  return `© ${dayjs().year()} `;
}

function getFooterCopyrightTail(props: FooterSectionProps): string {
  const description = getFooterDescription(props).trim();
  return description ? ` — ${description}` : "";
}

function getFooterCopyrightFullText(props: FooterSectionProps): string {
  return `${getFooterCopyrightLead(props)}${getFooterName(props)}${getFooterCopyrightTail(props)}`;
}

function scrollToTop(event?: MouseEvent<HTMLAnchorElement>): void {
  event?.preventDefault();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function isGroupedMenuCollectionItem(item: GeneratedFooterItem): boolean {
  return (
    "h60Text" in item ||
    "link0Link0Text" in item ||
    "link0Text2" in item ||
    "link0Link0Text2" in item
  );
}

function isSimpleMenuCollectionItem(item: GeneratedFooterItem): boolean {
  return (
    "link0Text" in item &&
    "href" in item &&
    !("className" in item) &&
    !("src" in item) &&
    !("link0Link0Text" in item) &&
    !("ariaLabel" in item)
  );
}

function isSocialIconCollectionItem(item: GeneratedFooterItem): boolean {
  return (
    typeof item.className === "string" &&
    item.className.includes("fa-") &&
    !("link0Text" in item) &&
    !("linkText" in item)
  );
}

function isContactCollectionItem(item: GeneratedFooterItem): boolean {
  if (typeof item.href !== "string") {
    return false;
  }

  return (
    (item.href.startsWith("mailto:") ||
      item.href.startsWith("tel:") ||
      item.href.startsWith("callto:")) &&
    ("link0Text" in item || "linkText" in item)
  );
}

function getFooterSocialNetworkLabel(
  item: FooterCommonDataSocialNetwork,
): string {
  return (
    item.Icon?.replace(/^.*fa-/, "")
      .replace(/-/g, " ")
      .trim() || "social"
  );
}

function applySocialCollection(
  defaults: GeneratedFooterItem[],
  props: FooterSectionProps,
): GeneratedFooterItem[] {
  const socialNetworks = getFooterSocialNetworks(props);
  if (socialNetworks.length === 0 || defaults.length === 0) {
    return defaults;
  }

  return socialNetworks.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};

    return {
      ...baseItem,
      key: item.Id,
      className: item.Icon,
      href: item.Link,
      ...(Object.prototype.hasOwnProperty.call(baseItem, "ariaLabel")
        ? { ariaLabel: getFooterSocialNetworkLabel(item) }
        : {}),
    };
  });
}

function getFooterContactCollectionItems(
  props: FooterSectionProps,
): GeneratedFooterItem[] {
  const items: GeneratedFooterItem[] = [];
  const primaryPhone = getFooterPrimaryPhone(props);
  const email = getFooterEmail(props);

  if (primaryPhone) {
    items.push({
      key: "phone",
      href: `tel:${primaryPhone}`,
      link0Text: primaryPhone,
      linkText: primaryPhone,
    });
  }

  if (email) {
    items.push({
      key: "email",
      href: `mailto:${email}`,
      link0Text: email,
      linkText: email,
    });
  }

  return items;
}

function applyContactCollection(
  defaults: GeneratedFooterItem[],
  props: FooterSectionProps,
): GeneratedFooterItem[] {
  const contactItems = getFooterContactCollectionItems(props);
  if (contactItems.length === 0 || defaults.length === 0) {
    return defaults;
  }

  return contactItems.map((contactItem, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};

    return {
      ...baseItem,
      key: contactItem.key,
      href: contactItem.href,
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Text")
        ? { link0Text: contactItem.link0Text }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "linkText")
        ? { linkText: contactItem.linkText }
        : {}),
    };
  });
}

function applyEmbeddedContactItem(
  item: GeneratedFooterItem,
  props: FooterSectionProps,
): GeneratedFooterItem {
  const email = getFooterEmail(props);
  const primaryPhone = getFooterPrimaryPhone(props);
  const address = getFooterAddress(props);
  const footerLogoUrl = getFooterLogoUrl(props);
  const footerLogoAlt = getFooterLogoAlt(props);

  const nextItem = { ...item };
  const iconClass = [
    typeof item.i0ClassName === "string" ? item.i0ClassName : "",
    typeof item.i0ClassName2 === "string" ? item.i0ClassName2 : "",
    typeof item.i0ClassName3 === "string" ? item.i0ClassName3 : "",
    typeof item.className === "string" ? item.className : "",
  ].join(" ");

  if (
    typeof item.href === "string" &&
    item.href.startsWith("mailto:") &&
    email
  ) {
    nextItem.href = `mailto:${email}`;
    if (Object.prototype.hasOwnProperty.call(item, "link0Text")) {
      nextItem.link0Text = email;
    }
    if (Object.prototype.hasOwnProperty.call(item, "linkText")) {
      nextItem.linkText = email;
    }
  }

  if (primaryPhone) {
    if (
      typeof item.href === "string" &&
      (item.href.startsWith("tel:") || item.href.startsWith("callto:"))
    ) {
      nextItem.href = `tel:${primaryPhone}`;
      if (Object.prototype.hasOwnProperty.call(item, "link0Text")) {
        nextItem.link0Text = primaryPhone;
      }
      if (Object.prototype.hasOwnProperty.call(item, "linkText")) {
        nextItem.linkText = primaryPhone;
      }
    }

    if (typeof item.h50H50Text === "string" || item.h50H50Text) {
      if (iconClass.includes("call")) {
        nextItem.h50H50Text = primaryPhone;
      }
    }
  }

  if (email && (iconClass.includes("message") || iconClass.includes("sms"))) {
    if (Object.prototype.hasOwnProperty.call(item, "p1Text")) {
      nextItem.p1Text = email;
    }
    if (Object.prototype.hasOwnProperty.call(item, "pText")) {
      nextItem.pText = email;
    }
  }

  if (primaryPhone && iconClass.includes("call")) {
    if (Object.prototype.hasOwnProperty.call(item, "p1Text")) {
      nextItem.p1Text = primaryPhone;
    }
    if (Object.prototype.hasOwnProperty.call(item, "pText")) {
      nextItem.pText = primaryPhone;
    }
  }

  if (address && iconClass.includes("location")) {
    if (Object.prototype.hasOwnProperty.call(item, "h50Text")) {
      nextItem.h50Text = address;
    }
    if (Object.prototype.hasOwnProperty.call(item, "p1Text")) {
      nextItem.p1Text = address;
    }
    if (Object.prototype.hasOwnProperty.call(item, "pText")) {
      nextItem.pText = address;
    }
  }

  if (
    footerLogoUrl &&
    typeof item.src === "string" &&
    item.src.includes("/logo-")
  ) {
    nextItem.src = footerLogoUrl;
    if (Object.prototype.hasOwnProperty.call(item, "alt")) {
      nextItem.alt = footerLogoAlt;
    }
  }

  return nextItem;
}

function isLikelyLogoProp(key: string, value: unknown): value is string {
  return (
    typeof value === "string" &&
    /ImageSrc$/.test(key) &&
    value.includes("/logo-")
  );
}

function isLikelyLogoAltProp(key: string, value: unknown): value is string {
  return typeof value === "string" && /ImageAlt$/.test(key);
}

function isLikelyAddressValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /(Aminaport|Marina Avenue|Sunset Boulevard|Highway|, USA|IC410)/i.test(
      value,
    )
  );
}

function isLikelyEmailValue(value: unknown): value is string {
  return typeof value === "string" && /@/.test(value);
}

function isLikelyPhoneValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /(\+\d|\d[\d\s-]{6,})/.test(value) &&
    !value.includes("@")
  );
}

function applyFooterSingletonOverrides<T extends GeneratedFooterProps>(
  generatedProps: T,
  props: FooterSectionProps,
): T {
  const nextProps: GeneratedFooterProps = {
    ...generatedProps,
  };
  const footerLogoUrl = getFooterLogoUrl(props);
  const footerLogoAlt = getFooterLogoAlt(props);
  const footerAddress = getFooterAddress(props);
  const footerEmail = getFooterEmail(props);
  const footerPhone = getFooterPrimaryPhone(props);
  const footerName = getFooterName(props);
  const footerCopyrightLead = getFooterCopyrightLead(props);
  const footerCopyrightTail = getFooterCopyrightTail(props);
  const footerCopyrightFullText = getFooterCopyrightFullText(props);

  for (const [key, value] of Object.entries(nextProps)) {
    if (isLikelyLogoProp(key, value) && footerLogoUrl) {
      nextProps[key] = footerLogoUrl;
      continue;
    }

    if (isLikelyLogoAltProp(key, value)) {
      const srcKey = key.replace(/Alt$/, "Src");
      if (isLikelyLogoProp(srcKey, nextProps[srcKey])) {
        nextProps[key] = footerLogoAlt;
        continue;
      }
    }

    if (typeof value !== "string") {
      continue;
    }

    if (/P0PText2$|DivText2$/.test(key) && /rights reserved/i.test(value)) {
      nextProps[key] = footerCopyrightTail;
      continue;
    }

    if (
      /P0PText$|DivText$/.test(key) &&
      /copyright|©|&copy;|all rights reserved/i.test(value)
    ) {
      const hasLinkedBrandText =
        key.replace(/P0PText$|DivText$/, "P0A0AText") in nextProps ||
        key.replace(/P0PText$|DivText$/, "P0Link0LinkText") in nextProps;
      nextProps[key] = hasLinkedBrandText
        ? footerCopyrightLead
        : footerCopyrightFullText;
      continue;
    }

    if (
      /A0AText$|Link0LinkText$|DivDataText$|DivText$/.test(key) &&
      /^doccure$/i.test(value.trim())
    ) {
      nextProps[key] = footerName;
      continue;
    }

    if (footerAddress && isLikelyAddressValue(value)) {
      nextProps[key] = footerAddress;
      continue;
    }

    if (footerEmail && isLikelyEmailValue(value)) {
      nextProps[key] = footerEmail;
      continue;
    }

    if (footerPhone && isLikelyPhoneValue(value)) {
      nextProps[key] = footerPhone;
      continue;
    }

    if (
      footerPhone &&
      (value.startsWith("tel:") || value.startsWith("callto:"))
    ) {
      nextProps[key] = `tel:${footerPhone}`;
      continue;
    }

    if (footerEmail && value.startsWith("mailto:")) {
      nextProps[key] = `mailto:${footerEmail}`;
    }
  }

  return nextProps as T;
}

function applyFooterCommonDataOverridesToValue(
  value: unknown,
  props: FooterSectionProps,
): unknown {
  // React elements (for example the linked franchise domain in the copyright)
  // may contain nested props and must not be traversed as plain data objects.
  if (isValidElement(value)) return value;

  if (Array.isArray(value)) {
    if (
      value.length === 0 ||
      typeof value[0] !== "object" ||
      value[0] === null
    ) {
      return value;
    }

    const firstItem = value[0] as GeneratedFooterItem;
    if (isGroupedMenuCollectionItem(firstItem)) {
      return value.map((item) =>
        typeof item === "object" && item !== null
          ? applyFooterCommonDataOverridesToItem(
            item as GeneratedFooterItem,
            props,
          )
          : item,
      );
    }

    if (isSocialIconCollectionItem(firstItem)) {
      return applySocialCollection(value as GeneratedFooterItem[], props);
    }

    if (isContactCollectionItem(firstItem)) {
      return applyContactCollection(value as GeneratedFooterItem[], props);
    }

    return value.map((item) =>
      typeof item === "object" && item !== null
        ? applyFooterCommonDataOverridesToItem(
          item as GeneratedFooterItem,
          props,
        )
        : item,
    );
  }

  if (typeof value === "object" && value !== null) {
    return applyFooterCommonDataOverridesToItem(
      value as GeneratedFooterItem,
      props,
    );
  }

  return value;
}

function applyFooterCommonDataOverridesToItem(
  item: GeneratedFooterItem,
  props: FooterSectionProps,
): GeneratedFooterItem {
  const nextItem = applyEmbeddedContactItem(item, props);

  for (const key of Object.keys(nextItem)) {
    nextItem[key] = applyFooterCommonDataOverridesToValue(nextItem[key], props);
  }

  return nextItem;
}

function applyFooterCommonDataOverrides<T extends GeneratedFooterProps>(
  generatedProps: T,
  props: FooterSectionProps,
): T {
  const nextProps: GeneratedFooterProps = {
    ...generatedProps,
  };

  for (const key of Object.keys(nextProps)) {
    nextProps[key] = applyFooterCommonDataOverridesToValue(
      nextProps[key],
      props,
    );
  }

  return nextProps as T;
}

function applyMenuToGroupedCollection(
  defaults: GeneratedFooterItem[],
  props: FooterSectionProps,
): GeneratedFooterItem[] {
  const menus = getFooterMenus(props);
  if (menus.length === 0 || defaults.length === 0) {
    return defaults;
  }

  return menus.map((menu, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};
    const menuLinks = menu.Items ?? [];

    return {
      ...baseItem,
      key: menu.Id,
      ...(Object.prototype.hasOwnProperty.call(baseItem, "h60Text")
        ? { h60Text: menu.Title }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Text")
        ? { link0Text: menuLinks[0]?.Title ?? menu.Title }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "href")
        ? { href: menuLinks[0]?.Link ?? "#" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Link0Text")
        ? { link0Link0Text: menuLinks[1]?.Title ?? "" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Href")
        ? { link0Href: menuLinks[1]?.Link ?? "#" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Text2")
        ? { link0Text2: menuLinks[2]?.Title ?? "" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "href2")
        ? { href2: menuLinks[2]?.Link ?? "#" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Link0Text2")
        ? { link0Link0Text2: menuLinks[2]?.Title ?? "" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Href2")
        ? { link0Href2: menuLinks[2]?.Link ?? "#" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Link0Text3")
        ? { link0Link0Text3: menuLinks[3]?.Title ?? "" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Href3")
        ? { link0Href3: menuLinks[3]?.Link ?? "#" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Link0Text4")
        ? { link0Link0Text4: menuLinks[4]?.Title ?? "" }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(baseItem, "link0Href4")
        ? { link0Href4: menuLinks[4]?.Link ?? "#" }
        : {}),
    };
  });
}

function applyMenuToSimpleCollection(
  defaults: GeneratedFooterItem[],
  props: FooterSectionProps,
): GeneratedFooterItem[] {
  const menuLinks = getFooterMenuLinks(props);
  if (menuLinks.length === 0 || defaults.length === 0) {
    return defaults;
  }

  return menuLinks.map((item, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};

    return {
      ...baseItem,
      key: item.Id,
      link0Text: item.Title,
      href: item.Link,
    };
  });
}

function applyFooterMenuOverrides<T extends GeneratedFooterProps>(
  generatedProps: T,
  props: FooterSectionProps,
): T {
  const nextProps: GeneratedFooterProps = {
    ...generatedProps,
  };

  let groupedApplied = false;
  let simpleApplied = false;

  for (const key of Object.keys(nextProps)) {
    const value = nextProps[key];
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      typeof value[0] !== "object" ||
      value[0] === null
    ) {
      continue;
    }

    const firstItem = value[0] as GeneratedFooterItem;

    if (!groupedApplied && isGroupedMenuCollectionItem(firstItem)) {
      nextProps[key] = applyMenuToGroupedCollection(
        value as GeneratedFooterItem[],
        props,
      );
      groupedApplied = true;
      continue;
    }

    if (
      !simpleApplied &&
      /^(footerSectionUls|footerSectionUls2)$/.test(key) &&
      isSimpleMenuCollectionItem(firstItem)
    ) {
      nextProps[key] = applyMenuToSimpleCollection(
        value as GeneratedFooterItem[],
        props,
      );
      simpleApplied = true;
    }
  }

  return nextProps as T;
}

function applyFooterGeneratedOverrides<T extends GeneratedFooterProps>(
  generatedProps: T,
  props: FooterSectionProps,
): T {
  return applyFooterSocialIconRenderers(
    applyFooterSingletonOverrides(
      applyFooterCommonDataOverrides(
        applyFooterMenuOverrides(generatedProps, props),
        props,
      ),
      props,
    ),
    props,
  );
}

function getFooterSocialNetworkMap(props: FooterSectionProps) {
  return new Map(
    getFooterSocialNetworks(props).map(
      (item) => [String(item.Id), item] as const,
    ),
  );
}

function getCollectionLinkRendererKey(collectionKey: string): string {
  return `${collectionKey}LinkRenderer`;
}

function getCollectionRendererKey(collectionKey: string): string {
  return `${collectionKey}Renderer`;
}

function applyFooterSocialIconRenderers<T extends GeneratedFooterProps>(
  generatedProps: T,
  props: FooterSectionProps,
): T {
  const nextProps: GeneratedFooterProps = {
    ...generatedProps,
  };
  const socialNetworkMap = getFooterSocialNetworkMap(props);

  for (const key of Object.keys(nextProps)) {
    const value = nextProps[key];
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      typeof value[0] !== "object" ||
      value[0] === null
    ) {
      continue;
    }

    const firstItem = value[0] as GeneratedFooterItem;
    if (!isSocialIconCollectionItem(firstItem)) {
      continue;
    }

    const renderSocialItem = (socialNetwork: FooterCommonDataSocialNetwork) => (
      <SmartLink href={socialNetwork.Link}>
        <Icon icon={socialNetwork.Icon} />
      </SmartLink>
    );

    nextProps[getCollectionLinkRendererKey(key)] = (
      item: GeneratedFooterItem,
    ) => {
      const socialNetwork = socialNetworkMap.get(String(item.key));
      if (!socialNetwork) {
        return null;
      }

      return renderSocialItem(socialNetwork);
    };

    const linkRendererKey = getCollectionLinkRendererKey(key);
    if (!(linkRendererKey in generatedProps)) {
      nextProps[getCollectionRendererKey(key)] = () => {
        const socialNetworks = getFooterSocialNetworks(props);

        if (/^footerSectionUls/.test(key)) {
          return (
            <>
              {socialNetworks.map((socialNetwork) => (
                <li key={socialNetwork.Id}>
                  {renderSocialItem(socialNetwork)}
                </li>
              ))}
            </>
          );
        }

        return socialNetworks.map((socialNetwork) => (
          <SmartLink href={socialNetwork.Link} key={socialNetwork.Id}>
            <Icon icon={socialNetwork.Icon} />
          </SmartLink>
        ));
      };
    }
  }

  return nextProps as T;
}

const generatedFooterRenderers: Record<string, GeneratedFooterRenderer> = {
  [FooterAppearance.Home1.key]: (props, t) =>
    (() => {
      const footerMenu = getFooterMenusByLocation(
        props,
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
          .key,
      )[0];
      const usefulLinkMenu = getFooterMenusByLocation(
        props,
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
          .UsefulLinkMenu.key,
      )[0];
      const socialNetworkMap = new Map(
        props.pageData.CommonData.SocialNetworks.map(
          (item) => [String(item.Id), item] as const,
        ),
      );
      const footerSettingLinks = getFooterSettingLinks(props);

      return (
        <HomeOneFooterSectionGenerated
          {...buildGeneratedFooterProps<HomeOneFooterSectionGeneratedProps>(
            "HomeOneFooterSection",
            {
              // Example: this is the place to override generated props with runtime values.
              footerDiv0Div0Div0Div1Div0P0PText: getFooterDescription(props),
              footerDiv1Div0Div0Div0P0PText: getFooterCopyrightFullText(props),
              footerDiv0Div0Div0Div1Div0H60H6Text: t(
                "landing.footer.newsletter",
                undefined,
                "Newsletter",
              ),
              footerDiv0Div0Div0Div1Div0Div1H60H6Text: t(
                "landing.footer.connectWithUs",
                undefined,
                "Connect With Us",
              ),
              footerSection336s: [footerMenu, usefulLinkMenu]
                .filter((menu): menu is NonNullable<typeof menu> => !!menu)
                .map((menu) => ({
                  key: menu.Id,
                  h60Text: menu.Title,
                  link0Text: menu.Items[0]?.Title ?? menu.Title,
                  href: menu.Items[0]?.Link ?? "#",
                  link0Link0Text: menu.Items[1]?.Title ?? "",
                  link0Href: menu.Items[1]?.Link ?? "#",
                  link0Link0Text2: menu.Items[2]?.Title ?? "",
                  link0Href2: menu.Items[2]?.Link ?? "#",
                  link0Link0Text3: menu.Items[3]?.Title ?? "",
                  link0Href3: menu.Items[3]?.Link ?? "#",
                  link0Link0Text4: menu.Items[4]?.Title ?? "",
                  link0Href4: menu.Items[4]?.Link ?? "#",
                  className: "col-lg-3 col-md-3 col-sm-6",
                  div0ClassName: "footer-widget footer-menu",
                  h60ClassName: "footer-title",
                })),
              footerSectionUls5: props.pageData.CommonData.SocialNetworks.map(
                (item) => ({
                  key: item.Id,
                  className: item.Icon,
                  href: item.Link,
                }),
              ),
              footerSectionUls6: footerSettingLinks.map((item, index) => ({
                key: item.Id || `footer-link-${index}`,
                link0Text: item.Title ?? "",
                href: item.Link ?? "#",
              })),
              footerDiv0Div0Div0Div1Div0Div0Form0FormRenderer: () => (
                <FooterNewsletterForm
                  buttonClassName="btn btn-md btn-primary-gradient d-inline-flex align-items-center"
                  buttonContent={
                    <>
                      <i className="isax isax-send-25 me-1" />
                      {t("landing.footer.send", undefined, "Send")}
                    </>
                  }
                />
              ),
            },
          )}
          footerSection336sRenderer={() => {
            const groupedMenus = [
              {
                key: CenterOptions.SiteTemplate
                  .CenterSiteTemplateMenuLocationType.FooterMenu.key,
                menu: footerMenu,
              },
              {
                key: CenterOptions.SiteTemplate
                  .CenterSiteTemplateMenuLocationType.UsefulLinkMenu.key,
                menu: usefulLinkMenu,
              },
            ].filter((item) => !!item.menu);

            return (
              <>
                {groupedMenus.map(({ key, menu }) => (
                  <div className="col-lg-3 col-md-3 col-sm-6" key={key}>
                    <div className="footer-widget footer-menu">
                      <h6 className="footer-title">{menu.Title}</h6>
                      <ul>
                        {menu.Items.map((item) => (
                          <li key={item.Id}>
                            <SmartLink href={item.Link}>{item.Title}</SmartLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
                {getFooterTrustSealHtml(props) ? (
                  <div className="col-lg-3 col-md-3 col-sm-6" key="trust-seal-html">
                    <div className="footer-widget footer-menu trust-seal-html">
                      <Html value={getFooterTrustSealHtml(props)} />
                    </div>
                  </div>
                ) : null}
              </>
            );
          }}
          footerSectionUls5LinkRenderer={(item) => {
            const socialNetwork = socialNetworkMap.get(String(item.key));
            if (!socialNetwork) {
              return null;
            }

            return (
              <SmartLink href={socialNetwork.Link}>
                <Icon icon={socialNetwork.Icon} />
              </SmartLink>
            );
          }}
        />
      );
    })(),
  [FooterAppearance.Home2.key]: (props, t) => {
    const footerMenuLinks = getFooterMenuLinks(props);
    const socialNetworkMap = new Map(
      props.pageData.CommonData.SocialNetworks.map(
        (item) => [String(item.Id), item] as const,
      ),
    );
    const footerSettingLinks = getFooterSettingLinks(props);
    const generatedProps =
      buildGeneratedFooterProps<HomeTwoFooterSectionGeneratedProps>(
        "HomeTwoFooterSection",
        {
          footerDiv1Div0Div0Div0P0PText: getFooterCopyrightFullText(props),
          footerSectionUls3: footerSettingLinks.map((item, index) => ({
            key: item.Id || `footer-link-${index}`,
            link0Text: item.Title ?? "",
            href: item.Link ?? "#",
          })),
        },
      );
    const contactInfos = [
      props.pageData.CommonData.Email
        ? {
          key: "email",
          link0Text: props.pageData.CommonData.Email,
          href: `mailto:${props.pageData.CommonData.Email}`,
          span0Text: t(
            "landing.footer.generalInquiries",
            undefined,
            "General Inquiries",
          ),
          className: "contact-info",
        }
        : null,
      !_.isEmpty(props.pageData.CommonData.Tels)
        ? {
          key: "tel",
          link0Text: props.pageData.CommonData.Tels[0],
          href: `tel:${props.pageData.CommonData.Tels[0]}`,
          span0Text: t("landing.footer.callUsOn", undefined, "Call us on"),
          className: "contact-info",
        }
        : null,
    ].filter(
      (
        item,
      ): item is {
        key: string;
        link0Text: string;
        href: string;
        span0Text: string;
        className: string;
      } => item !== null,
    );

    return (
      <HomeTwoFooterSectionGenerated
        {...generatedProps}
        footerSection436sRenderer={() => (
          <>
            <div className="col-xl-4 col-lg-3 col-md-6">
              <div className="footer-widget footer-about">
                <div className="footer-logo">
                  <SmartImage
                    alt={
                      props.pageData.CommonData.Image?.AltText ??
                      getFooterName(props)
                    }
                    src={
                      props.pageData.CommonData.Image?.Url ??
                      "/Templates/Doccure/img/logo-02.svg"
                    }
                  />
                </div>
                <div className="footer-about-content">
                  <p>{getFooterDescription(props)}</p>
                  <div className="social-icon">
                    <ul>
                      {props.pageData.CommonData.SocialNetworks.map((item) => (
                        <li key={item.Id}>
                          <SmartLink href={item.Link}>
                            <Icon icon={item.Icon} />
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {footerMenuLinks.length > 0
                    ? t("landing.footer.mainMenu", undefined, "Main Menu")
                    : t("landing.footer.company", undefined, "Company")}
                </h5>
                <ul className="footer-menu">
                  {footerMenuLinks.map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>{item.Title}</SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="footer-widget footer-contact">
                <h5 className="footer-title">
                  {t("landing.footer.needHelp", undefined, "Need help?")}
                </h5>
                {getFooterAddress(props) ? (
                  <div className="contact-info">
                    <span>
                      {t(
                        "landing.footer.visitOurClinic",
                        undefined,
                        "Visit Our Clinic",
                      )}
                    </span>
                    <p>{getFooterAddress(props)}</p>
                  </div>
                ) : null}
                {contactInfos.map((item) => (
                  <div className={item.className} key={item.key}>
                    <span>{item.span0Text}</span>
                    <p>
                      <SmartLink href={item.href}>{item.link0Text}</SmartLink>
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {t(
                    "landing.footer.keepInTouchWithUs",
                    undefined,
                    "Keep in touch with us",
                  )}
                </h5>
                <div className="footer-subscribe">
                  <p>
                    {t(
                      "landing.footer.subscribeDescription",
                      undefined,
                      "Subscribe our newsletter to get the latest news and updates From Doccure !",
                    )}
                  </p>
                  <div className="subscribe-input">
                    <FooterNewsletterForm
                      buttonClassName="btn-icon btn btn-primary-gradient"
                      buttonContent={<i className="isax isax-arrow-right-1" />}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      />
    );
  },
  [FooterAppearance.Home3.key]: (props) => {
    const defaults =
      buildGeneratedFooterProps<HomeThreeFooterSectionGeneratedProps>(
        "HomeThreeFooterSection",
      );
    const slogans = getFooterSettingSlogans(props);
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const groupedMenus = [footerMenu, usefulLinkMenu].filter(
      (menu): menu is NonNullable<typeof menu> => Boolean(menu),
    );

    return (
      <HomeThreeFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0Div0Link0Span0SpanText:
              getFooterName(props),
            footerSection66s:
              groupedMenus.length > 0
                ? groupedMenus.map((menu, index) => {
                  const baseItem =
                    defaults.footerSection66s[
                    index % defaults.footerSection66s.length
                    ];

                  return {
                    ...baseItem,
                    key: menu.Id,
                    h30Text: menu.Title,
                    footerWidgetItems: menu.Items.map((item) => ({
                      key: item.Id,
                      link0Text: item.Title,
                      href: item.Link,
                    })),
                  };
                })
                : defaults.footerSection66s,
            footerSectionServicesSlides:
              slogans.length > 0
                ? slogans.map((item, index) => {
                  const baseItem =
                    defaults.footerSectionServicesSlides[
                    index % defaults.footerSectionServicesSlides.length
                    ];

                  return {
                    ...baseItem,
                    key: item.Id || `footer-slogan-${index}`,
                    h20Text: item.Title ?? "",
                  };
                })
                : defaults.footerSectionServicesSlides,
            footerDiv0Div0Div0Div0Div0H20H2Renderer: () => (
              <h2 className="section-title">{getFooterName(props)}</h2>
            ),
          },
          props,
        )}
      />
    );
  },
  [FooterAppearance.Home4.key]: (props, t) =>
    (() => {
      const footerMenu = getFooterMenusByLocation(
        props,
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
          .key,
      )[0];
      const footerMenuLinks = getFooterMenuLinks(props);
      const defaults =
        buildGeneratedFooterProps<HomeFourFooterSectionGeneratedProps>(
          "HomeFourFooterSection",
        );
      const subscribeTitle = t(
        "landing.footer.subscribe",
        undefined,
        "Subscribe",
      );
      const subscribeDescription = t(
        "landing.footer.newsletterDescription",
        undefined,
        "Our conversation is just getting started",
      );

      return (
        <HomeFourFooterSectionGenerated
          {...applyFooterGeneratedOverrides(
            {
              ...defaults,
              footerDiv0Div0Div0Div0Div0P0PText: getFooterDescription(props),
              footerDiv0Div0Div0Div0Div0Div0Link0LinkHref: "/",
              footerDiv0Div0Div0Div1H40H4Text:
                footerMenu?.Title ?? defaults.footerDiv0Div0Div0Div1H40H4Text,
              footerSectionUls:
                footerMenuLinks.length > 0
                  ? footerMenuLinks.map((item) => ({
                    key: item.Id,
                    link0Text: item.Title,
                    href: item.Link,
                  }))
                  : defaults.footerSectionUls,
              footerDiv0Div0Div0Div2H40H4Text: subscribeTitle,
              footerDiv0Div0Div0Div2H40H4Renderer: () => (
                <div className="footer-subscribe">
                  <h4 className="footer-title">{subscribeTitle}</h4>
                  <p>{subscribeDescription}</p>
                </div>
              ),
              footerDiv0Div0Div0Div2Form0Button0ButtonText: subscribeTitle,
            },
            props,
          )}
          footerDiv0Div0Div0Div2Form0FormRenderer={() => (
            <FooterNewsletterForm
              wrapClassName="subscribe-input"
              buttonClassName="btn-icon btn btn-secondary"
              placeholder={t(
                "landing.footer.emailAddress",
                undefined,
                "Email address",
              )}
              buttonContent={<i className="isax isax-arrow-right-1" />}
            />
          )}
        />
      );
    })(),
  [FooterAppearance.Home5.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeFiveFooterSectionGeneratedProps>(
        "HomeFiveFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const footerMenuLinks = footerMenu?.Items ?? [];
    const socialNetworks = getFooterSocialNetworks(props);
    const primaryPhone = getFooterPrimaryPhone(props);
    const email = getFooterEmail(props);
    const address = getFooterAddress(props);
    const slogans = getFooterSettingSlogans(props);

    return (
      <HomeFiveFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerSectionServicesSlides:
              slogans.length > 0
                ? slogans.map((item, index) => {
                  const baseItem =
                    defaults.footerSectionServicesSlides[
                    index % defaults.footerSectionServicesSlides.length
                    ];

                  return {
                    ...baseItem,
                    key: item.Id || `footer-slogan-${index}`,
                    h20Text: item.Title ?? "",
                  };
                })
                : defaults.footerSectionServicesSlides,
          },
          props,
        )}
        footerSection336sRenderer={() => (
          <>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget footer-about">
                <div className="footer-logo">
                  <SmartImage
                    src={
                      getFooterLogoUrl(props) ||
                      "/Templates/Doccure/img/logo-white-05.svg"
                    }
                    alt={getFooterLogoAlt(props)}
                  />
                </div>
                <div className="footer-about-content">
                  <ul className="social-icon">
                    {socialNetworks.map((item) => (
                      <li key={item.Id}>
                        <SmartLink href={item.Link}>
                          <Icon icon={item.Icon} />
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="footer-widget divide-line">
                <h5 className="footer-title">
                  {footerMenu?.Title ??
                    t("landing.footer.company", undefined, "Company")}
                </h5>
                <ul className="footer-menu">
                  {footerMenuLinks.map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>{item.Title}</SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget footer-contact divide-line">
                <h5 className="footer-title">
                  {t("landing.footer.contact", undefined, "Contact")}
                </h5>
                <div className="contact-info">
                  <span className="title">
                    {t(
                      "landing.footer.callOrEmail",
                      undefined,
                      "Call / E-mail",
                    )}
                  </span>
                  {primaryPhone ? (
                    <p className="mb-1">
                      <SmartLink href={`tel:${primaryPhone}`}>
                        {primaryPhone}
                      </SmartLink>
                    </p>
                  ) : null}
                  {email ? (
                    <p>
                      <SmartLink href={`mailto:${email}`}>{email}</SmartLink>
                    </p>
                  ) : null}
                </div>
                <div className="contact-info mb-0">
                  <span className="title">
                    {t("landing.footer.ourAddress", undefined, "Our Address")}
                  </span>
                  <p>{address}</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {t("landing.footer.newsletter", undefined, "Newsletter")}
                </h5>
                <div className="footer-subscribe">
                  <p>{getFooterDescription(props)}</p>
                  <div className="subscribe-input">
                    <FooterNewsletterForm
                      buttonClassName="btn-icon btn btn-primary-gradient"
                      buttonContent={<i className="isax isax-arrow-right-1" />}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      />
    );
  },
  [FooterAppearance.Home6.key]: (props) => {
    const { first, second } = getFooterSettingLinkPair(props);
    const defaults =
      buildGeneratedFooterProps<HomeSixFooterSectionGeneratedProps>(
        "HomeSixFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const groupedMenus = [footerMenu, usefulLinkMenu].filter(
      (menu): menu is NonNullable<typeof menu> => Boolean(menu),
    );
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );

    return (
      <HomeSixFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0Div0ImageWithBasePath0ImageSrc:
              getFooterLogoUrl(props) ||
              defaults.footerDiv0Div0Div0Div0Div0Div0ImageWithBasePath0ImageSrc,
            footerDiv0Div0Div0Div0Div0Div0ImageWithBasePath0ImageAlt:
              getFooterLogoAlt(props),
            footerDiv0Div0Div0Div0Div0P0PText: getFooterDescription(props),
            footerDiv0Div0Div0Div0Div0P0Span0SpanText: "",
            footerSectionUls: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
            })),
            footerSection244s:
              groupedMenus.length > 0
                ? groupedMenus.map((menu, index) => {
                  const baseItem =
                    defaults.footerSection244s[
                    index % defaults.footerSection244s.length
                    ];

                  return {
                    ...baseItem,
                    key: menu.Id,
                    h50Text: menu.Title,
                    footerMenuItems: menu.Items.map((item) => ({
                      key: item.Id,
                      link0Text: item.Title,
                      href: item.Link,
                    })),
                  };
                })
                : defaults.footerSection244s,
            footerDiv1Div0Div0Div0Div1Div0Link0LinkHref: first?.Link ?? "#",
            footerDiv1Div0Div0Div0Div1Div0Link0LinkText: first?.Title ?? "",
            footerDiv1Div0Div0Div0Div1Div0Link0LinkVisible: Boolean(first),
            footerDiv1Div0Div0Div0Div1Div0Link1LinkHref: second?.Link ?? "#",
            footerDiv1Div0Div0Div0Div1Div0Link1LinkText: second?.Title ?? "",
            footerDiv1Div0Div0Div0Div1Div0Link1LinkVisible: Boolean(second),
            footerDiv1Div0Div0Div0Div1Div0Span0SpanText:
              first && second ? "/" : "",
          },
          props,
        )}
        footerSectionUlsLinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home7.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeSevenFooterSectionGeneratedProps>(
        "HomeSevenFooterSection",
      );
    const footerMenuLinks =
      getFooterMenusByLocation(
        props,
        CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
          .key,
      )[0]?.Items ?? getFooterMenuLinks(props);
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const primaryPhone = getFooterPrimaryPhone(props);
    const email = getFooterEmail(props);
    const address = getFooterAddress(props);

    return (
      <HomeSevenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv2Div0Div0Div0DivText: getFooterCopyrightFullText(props),
            footerSectionUls: footerMenuLinks.map((item) => ({
              key: item.Id,
              link0Text: item.Title,
              href: item.Link,
            })),
            footerSectionUls2: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
            })),
          },
          props,
        )}
        footerSection336sRenderer={() => (
          <>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget footer-about">
                <div className="footer-logo">
                  <SmartLink href="/">
                    <SmartImage
                      className="img-fluid"
                      alt={getFooterLogoAlt(props)}
                      src={
                        getFooterLogoUrl(props) ||
                        "/Templates/Doccure/img/logo-07.svg"
                      }
                    />
                  </SmartLink>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget footer-contact">
                <h5 className="footer-title">{getFooterName(props)}</h5>
                <p>{address}</p>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="footer-widget footer-contact">
                <h5 className="footer-title">
                  {t("landing.footer.contact", undefined, "Contact")}
                </h5>
                {primaryPhone ? <p>{primaryPhone}</p> : null}
                {email ? <p>{email}</p> : null}
              </div>
            </div>
          </>
        )}
        footerSectionUls2LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home8.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeEightFooterSectionGeneratedProps>(
        "HomeEightFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const slogans = getFooterSettingSlogans(props);
    const primaryPhone = getFooterPrimaryPhone(props);
    const email = getFooterEmail(props);
    const address = getFooterAddress(props);

    return (
      <HomeEightFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv2Div0Div0Div0P0PText: getFooterCopyrightFullText(props),
            footerSectionServicesSlides:
              slogans.length > 0
                ? slogans.flatMap((item, index) => {
                  const baseTextItem =
                    defaults.footerSectionServicesSlides[
                    (index * 2) %
                    defaults.footerSectionServicesSlides.length
                    ];
                  const baseSeparatorItem =
                    defaults.footerSectionServicesSlides[
                    (index * 2 + 1) %
                    defaults.footerSectionServicesSlides.length
                    ];

                  return [
                    {
                      ...baseTextItem,
                      key: `${item.Id || index}-text`,
                      h20Text: item.Title ?? "",
                      divClassName: "services-slide",
                      className: undefined,
                    },
                    {
                      ...baseSeparatorItem,
                      key: `${item.Id || index}-separator`,
                      h20Text: undefined,
                      divClassName: "services-slide",
                      className: "fa-solid fa-circle",
                    },
                  ];
                })
                : defaults.footerSectionServicesSlides,
            footerSectionUls3: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
              link0ClassName: "social-icon",
            })),
          },
          props,
        )}
        footerSectionServicesSlidesH2Renderer={(item, defaultNode) => {
          if (!item.className) {
            return defaultNode;
          }

          return <i className={item.className} />;
        }}
        footerSection346sRenderer={() => (
          <>
            <div className="col-xl-5 col-lg-12 col-md-6">
              <div className="footer-widget footer-about">
                <div className="footer-logo mb-4">
                  <SmartImage
                    src={
                      getFooterLogoUrl(props) ||
                      "/Templates/Doccure/img/logo-08.svg"
                    }
                    alt={getFooterLogoAlt(props)}
                  />
                </div>
                <div className="footer-about-content mb-3">
                  <p className="description">{getFooterDescription(props)}</p>
                </div>
                <div className="footer-available">
                  <h4 className="title">We Are Available !!</h4>
                  <p className="date">Monday - Friday : 8:30 AM to 6:30 PM</p>
                </div>
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {footerMenu?.Title ??
                    t("landing.footer.company", undefined, "Company")}
                </h5>
                <ul className="footer-menu">
                  {(footerMenu?.Items ?? []).map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>
                        <i className="isax isax-arrow-right-3" />
                        {item.Title}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {usefulLinkMenu?.Title ?? "Our Services"}
                </h5>
                <ul className="footer-menu">
                  {(usefulLinkMenu?.Items ?? []).map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>
                        <i className="isax isax-arrow-right-3" />
                        {item.Title}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="footer-widget">
                <h5 className="footer-title">
                  {t(
                    "landing.footer.keepInTouchWithUs",
                    undefined,
                    "Keep in touch with us",
                  )}
                </h5>
                <div className="footer-support">
                  <div className="support-item mb-3">
                    <div className="avatar avatar-lg bg-secondary rounded-circle flex-shrink-0">
                      <i className="isax isax-location-tick4" />
                    </div>
                    <div>
                      <p className="title">
                        {t(
                          "landing.footer.visitOurClinic",
                          undefined,
                          "Visit Our Clinic",
                        )}
                      </p>
                      <h5 className="link">{address}</h5>
                    </div>
                  </div>
                  <div className="support-item mb-3">
                    <div className="avatar avatar-lg bg-secondary rounded-circle flex-shrink-0">
                      <i className="isax isax-messages-3" />
                    </div>
                    <div>
                      <p className="title">
                        {t(
                          "landing.footer.generalInquiries",
                          undefined,
                          "General Inquiries",
                        )}
                      </p>
                      <h5 className="link">
                        <SmartLink href={`mailto:${email}`}>{email}</SmartLink>
                      </h5>
                    </div>
                  </div>
                  <div className="support-item mb-3">
                    <div className="avatar avatar-lg bg-secondary rounded-circle flex-shrink-0">
                      <i className="isax isax-call-calling" />
                    </div>
                    <div>
                      <p className="title">
                        {t("landing.footer.phone", undefined, "Phone")}
                      </p>
                      <h5 className="link">{primaryPhone}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        footerSectionUls3LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link} className="social-icon">
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home9.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeNineFooterSectionGeneratedProps>(
        "HomeNineFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const groupedMenus = [footerMenu, usefulLinkMenu].filter(
      (menu): menu is NonNullable<typeof menu> => Boolean(menu),
    );
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const email = getFooterEmail(props);
    const primaryPhone = getFooterPrimaryPhone(props);

    return (
      <HomeNineFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0H20H2Text:
              props.pageData.CommonData.Name ||
              defaults.footerDiv0Div0Div0Div0Div0H20H2Text,
            footerDiv1Div0Div0Div0P0PText: getFooterCopyrightFullText(props),
            footerDiv1Div0Link0LinkHref: "#",
            footerSectionSupports: [
              {
                ...defaults.footerSectionSupports[0],
                key: "email",
                className: "isax isax-messages-3",
                span0Text: t("landing.footer.email", undefined, "Email"),
                link0Text: email,
                href: email ? `mailto:${email}` : "#",
              },
              {
                ...(defaults.footerSectionSupports[1] ??
                  defaults.footerSectionSupports[0]),
                key: "phone",
                className: "isax isax-call-calling",
                span0Text: t("landing.footer.mobile", undefined, "Mobile"),
                link0Text: primaryPhone,
                href: primaryPhone
                  ? `tel:${primaryPhone.replace(/\s+/g, "")}`
                  : "#",
              },
            ],
            footerSection336s:
              groupedMenus.length > 0
                ? groupedMenus.map((menu, index) => {
                  const baseItem =
                    defaults.footerSection336s[
                    index % defaults.footerSection336s.length
                    ];

                  return {
                    ...baseItem,
                    key: menu.Id,
                    h30Text: menu.Title,
                    footerMenuItems: menu.Items.map((item) => ({
                      key: item.Id,
                      link0Text: item.Title,
                      href: item.Link,
                    })),
                  };
                })
                : defaults.footerSection336s,
            footerSectionUls3: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
              link0ClassName: "social-icon",
            })),
          },
          props,
        )}
        footerSectionUls3LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink
              href={socialNetwork.Link}
              className={item.link0ClassName ?? "social-icon"}
            >
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
        scrollToTop={scrollToTop}
      />
    );
  },
  [FooterAppearance.Home10.key]: (props, t, landingAppointmentModal) => {
    const defaults =
      buildGeneratedFooterProps<HomeTenFooterSectionGeneratedProps>(
        "HomeTenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const groupedMenus = [footerMenu, usefulLinkMenu].filter(
      (menu): menu is NonNullable<typeof menu> => Boolean(menu),
    );
    const centerId = props.pageData.CenterId;

    return (
      <HomeTenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0Div0Div0Link0LinkHref: "/",
            footerDiv0Div0Div0Div0Div0Div0Div0Link0ImageWithBasePath0ImageSrc:
              getFooterLogoUrl(props) ||
              defaults.footerDiv0Div0Div0Div0Div0Div0Div0Link0ImageWithBasePath0ImageSrc,
            footerDiv0Div0Div0Div0Div0Div0Div0Link0ImageWithBasePath0ImageAlt:
              getFooterLogoAlt(props),
            footerDiv0Div0Div0Div0Div0Div0P0PText:
              getFooterDescription(props) ||
              defaults.footerDiv0Div0Div0Div0Div0Div0P0PText,
            footerDiv0Div0Div0Div0Div0Div1Link0LinkText: t(
              "landing.footer.bookAppointment",
              undefined,
              "Book appointment",
            ),
            footerDiv0Div1Div0P0PText: getFooterCopyright(props, t),
            footerSection3336s:
              groupedMenus.length > 0
                ? groupedMenus.map((menu, index) => {
                  const baseItem =
                    defaults.footerSection3336s[
                    index % defaults.footerSection3336s.length
                    ];

                  return {
                    ...baseItem,
                    key: menu.Id,
                    h50Text: menu.Title,
                    footerMenuItems: menu.Items.map((item) => ({
                      key: item.Id,
                      link0Text: item.Title,
                      href: item.Link,
                    })),
                  };
                })
                : defaults.footerSection3336s,
          },
          props,
        )}
        footerDiv0Div0Div0Div0Div0Div1Link0LinkProps={{
          href: "#",
          onClick: async (e) => {
            e.preventDefault();
            await landingAppointmentModal.showBooking({
              values: {
                CenterId: centerId,
              },
            });
          },
        }}
      />
    );
  },
  [FooterAppearance.Home11.key]: (props, t, _landingAppointmentModal, serverRequestContext) => {
    const defaults =
      buildGeneratedFooterProps<HomeElevenFooterSectionGeneratedProps>(
        "HomeElevenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const email = getFooterEmail(props);
    const primaryPhone = getFooterPrimaryPhone(props);

    return (
      <HomeElevenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0H20H2Text: getFooterName(props),
            footerDiv0Div0Div0Div1Div0H30H3Text:
              footerMenu?.Title ??
              t("landing.footer.company", undefined, "Company"),
            footerDiv0Div0Div0Div2Div0H30H3Text: t(
              "landing.footer.contact",
              undefined,
              "Contact",
            ),
            footerDiv0Div0Div0Div2Div0Div0H40H4Text: t(
              "landing.footer.callOrEmail",
              undefined,
              "Call / Email",
            ),
            footerDiv0Div0Div0Div2Div0Div1H40H4Text: t(
              "landing.footer.ourAddress",
              undefined,
              "Our Address",
            ),
            footerDiv0Div0Div0Div2Div0Div1P0PText: getFooterAddress(props),
            footerDiv0Div0Div0Div0Div0Div0Form0Input0InputPlaceholder: t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            ),
            footerDiv1Div0Div0Div0Div0DivText: getFooterCopyrightForHome11(
              props,
              t,
              serverRequestContext,
            ),
            footerDiv1Div0Div0Div1Div0Div0DivDataText:
              getFooterAnimatedName(props),
            footerDiv1Div0Div0Div1Div0Div0DivText: getFooterAnimatedName(props),
            footerSectionUls:
              footerMenu?.Items.map((item) => ({
                key: item.Id,
                link0Text: item.Title,
                href: item.Link,
              })) ?? defaults.footerSectionUls,
            footerSectionDivs: [
              {
                key: "phone",
                linkText: primaryPhone,
                href: primaryPhone
                  ? `tel:${primaryPhone.replace(/\s+/g, "")}`
                  : "#",
              },
              {
                key: "email",
                linkText: email,
                href: email ? `mailto:${email}` : "#",
              },
            ],
            footerSectionUls2: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
            })),
          },
          props,
        )}
        footerDiv0Div0Div0Div0Div0Div0Form0FormRenderer={() => (
          <FooterNewsletterForm
            placeholder={t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            )}
            buttonClassName="btn-icon btn btn-secondary"
            buttonContent={<i className="isax isax-arrow-right-1" />}
          />
        )}
        footerSectionUls2LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home12.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeTwelveFooterSectionGeneratedProps>(
        "HomeTwelveFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const footerLinks = getFooterSettingLinks(props);
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const primaryPhone = getFooterPrimaryPhone(props);
    const email = getFooterEmail(props);
    const address = getFooterAddress(props);

    return (
      <HomeTwelveFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0Div0Div0Div0Div0H20H2Text: getFooterName(props),
            footerDiv0Div0Div0Div0Div0P0PText:
              getFooterDescription(props) ||
              defaults.footerDiv0Div0Div0Div0Div0P0PText,
            footerDiv0Div0Div0Div1Div0H40H4Text: t(
              "landing.footer.contact",
              undefined,
              "Contact",
            ),
            footerDiv0Div0Div0Div1Div0Div2P0PText: address,
            footerDiv0Div0Div0Div2Div0H40H4Text: t(
              "landing.footer.subscribeUpdates",
              undefined,
              "Subscribe to get new updates",
            ),
            footerDiv0Div0Div0Div2Div0H50H5Text: t(
              "landing.footer.joinOurCommunity",
              undefined,
              "Join our Community",
            ),
            footerDiv0Div0Div0Div2Div0P0PText: t(
              "landing.footer.newsletterDescription",
              undefined,
              "Get the latest news, offers, and insights delivered straight to your inbox.",
            ),
            footerDiv0Div0Div0Div2Div0Div0Div0Form0Input0InputPlaceholder: t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            ),
            footerDiv1Div0Div0Div0P0PText: getFooterCopyright(props, t),
            footerDiv1Link0LinkHref: "#",
            footerSectionSupports: [
              {
                key: "phone",
                className: "isax isax-call",
                link0Text: primaryPhone,
                href: primaryPhone
                  ? `tel:${primaryPhone.replace(/\s+/g, "")}`
                  : "#",
              },
              {
                key: "email",
                className: "isax isax-sms",
                link0Text: email,
                href: email ? `mailto:${email}` : "#",
              },
              {
                key: "address",
                className: "isax isax-location",
                link0Text: address,
                href: "#",
                divClassName: "support-item mb-0",
              },
            ],
            footerSectionUls: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
            })),
            footerSectionUls2:
              footerMenu?.Items.map((item) => ({
                key: item.Id,
                link0Text: item.Title,
                href: item.Link,
              })) ?? defaults.footerSectionUls2,
          },
          props,
        )}
        footerDiv0Div0Div0Div2Div0Div0Div0Form0FormRenderer={() => (
          <FooterNewsletterForm
            placeholder={t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            )}
            buttonClassName="btn-icon btn btn-secondary"
            buttonContent={<i className="isax isax-arrow-right-1" />}
          />
        )}
        footerSectionUlsLinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
        footerSectionSupportsRenderer={(defaultNode) => <>{defaultNode}</>}
        footerSectionSupportsLinkRenderer={(item, defaultNode) => {
          if (item.className !== "isax isax-location") {
            return defaultNode;
          }

          return <p className="text-truncate mb-0">{item.link0Text}</p>;
        }}
        footerSectionFooterSocialMenusRenderer={() => (
          <>
            <ul className="footer-social-menu">
              {(footerMenu?.Items ?? []).map((item) => (
                <li key={item.Id}>
                  <SmartLink href={item.Link}>{item.Title}</SmartLink>
                </li>
              ))}
            </ul>
            <ul className="footer-social-menu">
              {footerLinks.map((item, index) => (
                <li key={item.Id || `footer-link-${index}`}>
                  <SmartLink href={item.Link ?? "#"}>
                    {item.Title ?? ""}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </>
        )}
        scrollToTop={scrollToTop}
      />
    );
  },
  [FooterAppearance.Home13.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeThirteenFooterSectionGeneratedProps>(
        "HomeThirteenFooterSection",
      );
    const footerMenus = getFooterMenus(props)
      .filter((menu) => (menu.Items?.length ?? 0) > 0)
      .slice(0, 6);
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );

    return (
      <HomeThirteenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div1DivText: getFooterName(props),
            footerDiv1Div0Div0Div0P0PText: getFooterCopyrightFullText(props),
            footerSection2344s:
              footerMenus.length > 0
                ? footerMenus.map((menu, index) => {
                  const baseItem =
                    defaults.footerSection2344s[
                    index % defaults.footerSection2344s.length
                    ];

                  return {
                    ...baseItem,
                    key: menu.Id,
                    h40Text: menu.Title,
                    footerMenuItems: menu.Items.map((item) => ({
                      key: item.Id,
                      link0Text: item.Title,
                      href: item.Link,
                    })),
                  };
                })
                : defaults.footerSection2344s,
            footerSectionUls7: socialNetworks.map((item) => ({
              key: item.Id,
              className: item.Icon,
              href: item.Link,
            })),
          },
          props,
        )}
        footerSectionUls7LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home14.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeFourteenFooterSectionGeneratedProps>(
        "HomeFourteenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const footerLinks = getFooterSettingLinks(props);
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const email = getFooterEmail(props);
    const primaryPhone = getFooterPrimaryPhone(props);
    const address = getFooterAddress(props);

    return (
      <HomeFourteenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0DivText: getFooterName(props),
            footerDiv1Div0Div0Div0Div0H30H3Text:
              footerMenu?.Title ?? "Quick Links",
            footerDiv1Div0Div0Div1Div0H30H3Text: t(
              "landing.footer.contact",
              undefined,
              "Contact",
            ),
            footerDiv1Div0Div0Div1Div0Div0H40H4Text: t(
              "landing.footer.callOrEmail",
              undefined,
              "Call / Email",
            ),
            footerDiv1Div0Div0Div1Div0Div1H40H4Text: t(
              "landing.footer.ourAddress",
              undefined,
              "Our Address",
            ),
            footerDiv1Div0Div0Div1Div0Div1P0PText: address,
            footerDiv1Div0Div0Div2Div0H40H4Text: t(
              "landing.footer.joinOurCommunity",
              undefined,
              "Join our Community",
            ),
            footerDiv1Div0Div0Div2Div0P0PText: t(
              "landing.footer.newsletterDescription",
              undefined,
              "Get the latest news, offers, and insights delivered straight to your inbox.",
            ),
            footerDiv1Div0Div0Div2Div0Div0Form0Input0InputPlaceholder: t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            ),
            footerDiv2Div0Div0Link0LinkHref: "/",
            footerDiv2Div0Div0Link0ImageWithBasePath0ImageSrc:
              getFooterLogoUrl(props) ||
              defaults.footerDiv2Div0Div0Link0ImageWithBasePath0ImageSrc,
            footerDiv2Div0Div0Link0ImageWithBasePath0ImageAlt:
              getFooterLogoAlt(props),
            footerDiv2Div0Div1Div0P0PText: `Copyright © ${dayjs().year()} `,
            footerDiv2Div0Div1Div0P0Link0LinkHref: "/",
            footerDiv2Div0Div1Div0P0Link0LinkText: getFooterName(props),
            footerDiv2Div0Div1Div0P0PText2: ". All Rights Reserved",
            footerSectionDivs: [
              {
                key: "phone",
                link0Text: primaryPhone,
                href: primaryPhone
                  ? `tel:${primaryPhone.replace(/\s+/g, "")}`
                  : "#",
              },
              {
                key: "email",
                link0Text: email,
                href: email ? `mailto:${email}` : "#",
              },
            ],
            footerSectionUls:
              footerMenu?.Items.map((item) => ({
                key: item.Id,
                link0Text: item.Title,
                href: item.Link,
              })) ?? defaults.footerSectionUls,
            footerSectionUls2: socialNetworks.map((item) => ({
              key: item.Id,
              ariaLabel: item.Icon,
              className: item.Icon,
              href: item.Link,
            })),
            footerSectionUls3: footerLinks.map((item, index) => ({
              key: item.Id || `footer-link-${index}`,
              link0Text: item.Title ?? "",
              href: item.Link ?? "#",
            })),
          },
          props,
        )}
        footerDiv1Div0Div0Div2Div0Div0Form0FormRenderer={() => (
          <FooterNewsletterForm
            placeholder={t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            )}
            buttonClassName="btn-icon btn btn-secondary"
            buttonContent={<i className="isax isax-arrow-right-1" />}
          />
        )}
        footerSectionUls2LinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link} aria-label={item.ariaLabel}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
      />
    );
  },
  [FooterAppearance.Home15.key]: (props, t, landingAppointmentModal) => {
    const defaults =
      buildGeneratedFooterProps<HomeFifteenFooterSectionGeneratedProps>(
        "HomeFifteenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const socialNetworks = getFooterSocialNetworks(props);
    const socialNetworkMap = new Map(
      socialNetworks.map((item) => [String(item.Id), item] as const),
    );
    const email = getFooterEmail(props);
    const primaryPhone = getFooterPrimaryPhone(props);

    return (
      <HomeFifteenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div0H20H2Text: t(
              "landing.footer.letsTalk",
              undefined,
              "LET’S US TALK",
            ),
            footerDiv0Div0H20Span0SpanText: t(
              "landing.footer.together",
              undefined,
              "TOGETHER",
            ),
            footerDiv0Div1Div0Div0Div0Div0ImageWithBasePath0ImageSrc:
              getFooterLogoUrl(props) ||
              defaults.footerDiv0Div1Div0Div0Div0Div0ImageWithBasePath0ImageSrc,
            footerDiv0Div1Div0Div0Div0Div0ImageWithBasePath0ImageAlt:
              getFooterLogoAlt(props),
            footerDiv0Div1Div0Div0Div0Div1P0PText:
              getFooterDescription(props) ||
              defaults.footerDiv0Div1Div0Div0Div0Div1P0PText,
            footerDiv0Div1Div0Div1Div0H20H2Text:
              footerMenu?.Title ?? "Quick Links",
            footerDiv0Div1Div0Div2Div0Div0P0PText: email,
            footerDiv0Div1Div0Div2Div0Div0P1Link0LinkHref: primaryPhone
              ? `tel:${primaryPhone.replace(/\s+/g, "")}`
              : "#",
            footerDiv0Div1Div0Div2Div0Div0P1Link0LinkText: primaryPhone,
            footerDiv0Div1Div0Div2Div0Div1P0PText: t(
              "landing.footer.subscribeUpdates",
              undefined,
              "Subscribe to get new updates",
            ),
            footerDiv0Div1Div0Div2Div0Div1Div0Form0Input0InputPlaceholder: t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            ),
            footerDiv0Div2Div0P0PText: `Copyright © ${dayjs().year()} `,
            footerDiv0Div2Div0P0Link0LinkHref: "/",
            footerDiv0Div2Div0P0Link0LinkText: getFooterName(props),
            footerDiv0Div2Div0P0PText2: ". All Rights Reserved",
            footerLink0P0PText: t("landing.footer.start", undefined, "Start"),
            footerLink0P0Span0SpanText: t(
              "landing.footer.booking",
              undefined,
              "Booking",
            ),
            footerSectionDivs: socialNetworks.map((item) => ({
              key: item.Id,
              ariaLabel: item.Icon,
              className: item.Icon,
              href: item.Link,
            })),
            footerSectionUls:
              footerMenu?.Items.map((item) => ({
                key: item.Id,
                link0Text: item.Title,
                href: item.Link,
              })) ?? defaults.footerSectionUls,
          },
          props,
        )}
        footerDiv0Div1Div0Div2Div0Div1Div0Form0FormRenderer={() => (
          <FooterNewsletterForm
            placeholder={t(
              "landing.footer.emailAddress",
              undefined,
              "Email address",
            )}
            buttonClassName="btn-icon btn btn-primary-gradient"
            buttonContent={<i className="isax isax-arrow-right-1" />}
          />
        )}
        footerSectionDivsLinkRenderer={(item) => {
          const socialNetwork = socialNetworkMap.get(String(item.key));
          if (!socialNetwork) {
            return null;
          }

          return (
            <SmartLink href={socialNetwork.Link} aria-label={item.ariaLabel}>
              <Icon icon={socialNetwork.Icon} />
            </SmartLink>
          );
        }}
        footerLink0LinkProps={{
          href: "#",
          onClick: async (e) => {
            e.preventDefault();
            await landingAppointmentModal.showBooking({
              values: {
                CenterId: props.pageData.CenterId,
              },
            });
          },
        }}
      />
    );
  },
  [FooterAppearance.Home16.key]: (props, t) => {
    const defaults =
      buildGeneratedFooterProps<HomeSixteenFooterSectionGeneratedProps>(
        "HomeSixteenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const socialNetworks = getFooterSocialNetworks(props);
    const slogans = getFooterSettingSlogans(props);

    return (
      <HomeSixteenFooterSectionGenerated
        {...applyFooterGeneratedOverrides(
          {
            ...defaults,
            footerDiv0Div1P0PText: `Copyright © ${dayjs().year()} `,
            footerDiv0Div1P0A0AHref: "/",
            footerDiv0Div1P0A0AText: getFooterName(props),
            footerDiv0Div1P0PText2: ". All Rights Reserved",
            footerSectionServicesSlides:
              slogans.length > 0
                ? slogans.map((item, index) => ({
                  key: item.Id || `footer-slogan-${index}`,
                  h30Text: item.Title ?? "",
                }))
                : defaults.footerSectionServicesSlides,
          },
          props,
        )}
        footerSection336sRenderer={() => (
          <>
            <div className="col-lg-2">
              <div className="footer-logo">
                <SmartLink href="/">
                  <SmartImage
                    src={
                      getFooterLogoUrl(props) ||
                      "/Templates/Doccure/img/logo-16.svg"
                    }
                    alt={getFooterLogoAlt(props)}
                    className="img-fluid"
                  />
                </SmartLink>
              </div>
            </div>
            <div className="col-lg-3 col-md-3 col-sm-6">
              <div className="footer-widget">
                <h2 className="footer-title">
                  {footerMenu?.Title ?? "Quick"} <span>Links</span>
                </h2>
                <ul className="footer-menu">
                  {(footerMenu?.Items ?? []).map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>
                        <span className="icon"></span>
                        {item.Title}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="footer-widget">
                <h2 className="footer-title">
                  {usefulLinkMenu?.Title ?? "Our"} <span>Solutions</span>
                </h2>
                <ul className="footer-menu">
                  {(usefulLinkMenu?.Items ?? []).map((item) => (
                    <li key={item.Id}>
                      <SmartLink href={item.Link}>
                        <span className="icon"></span>
                        {item.Title}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-4 col-md-5">
              <div className="footer-widget">
                <h2 className="footer-title">
                  {t("landing.footer.getIn", undefined, "Get in")}{" "}
                  <span>{t("landing.footer.touch", undefined, "Touch")}</span>
                </h2>
                <div className="footer-subscribe">
                  <p className="description">
                    {t(
                      "landing.footer.newsletterDescription",
                      undefined,
                      "Get the latest news, offers, and insights delivered straight to your inbox.",
                    )}
                  </p>
                  <FooterNewsletterForm
                    placeholder={t(
                      "landing.footer.emailAddress",
                      undefined,
                      "Email address",
                    )}
                    buttonClassName="btn-icon btn-primary"
                    buttonContent={<i className="isax isax-arrow-right-1" />}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        footerSectionIconsRenderer={() => (
          <>
            {socialNetworks.map((item) => (
              <SmartLink
                key={item.Id}
                href={item.Link}
                className="icon"
                aria-label={item.Icon}
              >
                <Icon icon={item.Icon} />
              </SmartLink>
            ))}
          </>
        )}
      />
    );
  },
  [FooterAppearance.Home17.key]: (props, t, landingAppointmentModal) => {
    const defaults =
      buildGeneratedFooterProps<HomeSeventeenFooterSectionGeneratedProps>(
        "HomeSeventeenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const footerLinks = getFooterSettingLinks(props);
    const socialNetworks = getFooterSocialNetworks(props);
    const address = getFooterAddress(props);
    const email = getFooterEmail(props);
    const primaryPhone = getFooterPrimaryPhone(props);

    return (
      <div
        onClickCapture={async (e) => {
          const target = e.target;
          if (!(target instanceof HTMLElement)) {
            return;
          }

          const trigger = target.closest(".theme-17-btn");
          if (!trigger) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          await landingAppointmentModal.showBooking({
            values: {
              CenterId: props.pageData.CenterId,
            },
          });
        }}
      >
        <HomeSeventeenFooterSectionGenerated
          {...applyFooterGeneratedOverrides(
            {
              ...defaults,
              footerDiv0Div0Div0Div0A0Span1SpanText: t(
                "landing.footer.bookAppointment",
                undefined,
                "Schedule Consultation",
              ),
              footerDiv0Div0Div0Div0H20H2Text: t(
                "landing.footer.home17Title",
                undefined,
                "Ready to begin your journey home to wellness?",
              ),
              footerDiv0Div0Div0Div0P0PText: t(
                "landing.footer.home17Description",
                undefined,
                "Your first consultation is complimentary. No pressure, no commitments — just a genuine conversation about your health and what's possible.",
              ),
              footerDiv0Div1Div0Div0Div0A0AHref: "/",
              footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageSrc:
                getFooterLogoUrl(props) ||
                defaults.footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageSrc,
              footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageAlt:
                getFooterLogoAlt(props),
              footerDiv0Div1Div0Div0Div0P0PText:
                getFooterDescription(props) ||
                defaults.footerDiv0Div1Div0Div0Div0P0PText,
              footerDiv0Div1Div0Div1Div0H30H3Text:
                usefulLinkMenu?.Title ??
                t("landing.footer.ourServices", undefined, "Our Services"),
              footerDiv0Div1Div0Div2Div0H30H3Text:
                footerMenu?.Title ??
                t("landing.footer.company", undefined, "Company"),
              footerDiv1Div0DivText: getFooterName(props),
              footerDiv1Div1P0PText: `Copyright © ${dayjs().year()} `,
              footerDiv1Div1P0A0AHref: "/",
              footerDiv1Div1P0A0AText: getFooterName(props),
              footerDiv1Div1P0PText2: ". All Rights Reserved",
              footerSectionFooterContacts: [
                {
                  key: "address",
                  className: "isax isax-location-tick",
                  p0Text: t(
                    "landing.footer.visitOurClinic",
                    undefined,
                    "Visit Our Clinic",
                  ),
                  p1Text: address,
                },
                {
                  key: "email",
                  className: "isax isax-messages-3",
                  p0Text: t("landing.footer.email", undefined, "Email"),
                  p1Text: email,
                },
                {
                  key: "phone",
                  className: "isax isax-call-calling",
                  p0Text: t("landing.footer.mobile", undefined, "Mobile"),
                  p1Text: primaryPhone,
                },
              ],
              footerSectionUls3: footerLinks.map((item, index) => ({
                key: item.Id || `footer-link-${index}`,
                a0Text: item.Title ?? "",
                href: item.Link ?? "#",
              })),
              footerSectionUls: socialNetworks.map((item) => ({
                key: item.Id,
                ariaLabel: item.Icon,
                className: item.Icon,
                href: item.Link,
              })),
              footerSectionUls2:
                usefulLinkMenu?.Items.map((item) => ({
                  key: item.Id,
                  a0Text: item.Title,
                  href: item.Link,
                })) ?? defaults.footerSectionUls2,
            },
            props,
          )}
          footerSectionUls2={
            usefulLinkMenu?.Items.map((item) => ({
              key: item.Id,
              a0Text: item.Title,
              href: item.Link,
            })) ?? defaults.footerSectionUls2
          }
          footerSectionUlsRenderer={() => (
            <>
              {socialNetworks.map((item) => (
                <li key={item.Id}>
                  <SmartLink href={item.Link} aria-label={item.Icon}>
                    <Icon icon={item.Icon} />
                  </SmartLink>
                </li>
              ))}
            </>
          )}
          footerDiv0Div0Div0Div0A0AHref="#"
        />
      </div>
    );
  },
  [FooterAppearance.Home18.key]: (props, t, landingAppointmentModal) => {
    const defaults =
      buildGeneratedFooterProps<HomeEighteenFooterSectionGeneratedProps>(
        "HomeEighteenFooterSection",
      );
    const footerMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType.FooterMenu
        .key,
    )[0];
    const usefulLinkMenu = getFooterMenusByLocation(
      props,
      CenterOptions.SiteTemplate.CenterSiteTemplateMenuLocationType
        .UsefulLinkMenu.key,
    )[0];
    const socialNetworks = getFooterSocialNetworks(props);

    return (
      <div
        onClickCapture={async (e) => {
          const target = e.target;
          if (!(target instanceof HTMLElement)) {
            return;
          }

          const trigger = target.closest(".theme-18-btn");
          if (!trigger) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          await landingAppointmentModal.showBooking({
            values: {
              CenterId: props.pageData.CenterId,
            },
          });
        }}
      >
        <HomeEighteenFooterSectionGenerated
          {...applyFooterGeneratedOverrides(
            {
              ...defaults,
              footerDiv0Div0Div0H20H2Text: t(
                "landing.footer.home18Title",
                undefined,
                "Ready to Start Your Recovery?",
              ),
              footerDiv0Div0Div0P0PText: t(
                "landing.footer.home18Description",
                undefined,
                "Let's talk about your goals and create a plan to get you moving better",
              ),
              footerDiv0Div0Div1A0AText: t(
                "landing.footer.bookAppointment",
                undefined,
                "Schedule Your Consultation",
              ),
              footerDiv0Div1Div0Div0Div0A0AHref: "/",
              footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageSrc:
                getFooterLogoUrl(props) ||
                defaults.footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageSrc,
              footerDiv0Div1Div0Div0Div0A0ImageWithBasePath0ImageAlt:
                getFooterLogoAlt(props),
              footerDiv0Div1Div0Div0P0PText:
                getFooterDescription(props) ||
                defaults.footerDiv0Div1Div0Div0P0PText,
              footerDiv1Div0P0PText: `Copyright © ${dayjs().year()} `,
              footerDiv1Div0P0A0AHref: "/",
              footerDiv1Div0P0A0AText: getFooterName(props),
              footerDiv1Div0P0PText2: ". All Rights Reserved",
              footerSectionIcons: socialNetworks.map((item) => ({
                key: item.Id,
                ariaLabel: item.Icon,
                className: item.Icon,
                href: item.Link,
              })),
              footerSection3457s: [
                {
                  ...(defaults.footerSection3457s[0] ?? {}),
                  key: "services",
                  h20Text: usefulLinkMenu?.Title ?? "Our Services",
                  footerMenuItems: (usefulLinkMenu?.Items ?? []).map(
                    (item) => ({
                      key: item.Id,
                      a0Text: item.Title,
                      href: item.Link,
                    }),
                  ),
                },
                {
                  ...(defaults.footerSection3457s[1] ??
                    defaults.footerSection3457s[0] ??
                    {}),
                  key: "company",
                  h20Text:
                    footerMenu?.Title ??
                    t("landing.footer.company", undefined, "Company"),
                  footerMenuItems: (footerMenu?.Items ?? []).map((item) => ({
                    key: item.Id,
                    a0Text: item.Title,
                    href: item.Link,
                  })),
                },
              ],
            },
            props,
          )}
          footerSectionIconsRenderer={() => (
            <>
              {socialNetworks.map((item) => (
                <SmartLink
                  key={item.Id}
                  href={item.Link}
                  className="icon"
                  aria-label={item.Icon}
                >
                  <Icon icon={item.Icon} />
                </SmartLink>
              ))}
            </>
          )}
          footerDiv0Div0Div1A0AHref="#"
          footerDiv1A0AHref="#"
          footerDiv1A0AAriaLabel="Scroll to top"
        />
      </div>
    );
  },
};

const GeneratedFooterSection: FC<FooterSectionProps> = (props) => {
  const t = useT();
  const landingAppointmentModal = useLandingAppointmentModal();
  const serverRequestContext = useServerRequestContext();

  return renderGeneratedSection(
    props,
    Object.fromEntries(
      Object.entries(generatedFooterRenderers).map(
        ([appearanceKey, renderer]) => [
          appearanceKey,
          (sectionProps: FooterSectionProps) =>
            renderer(
              sectionProps,
              t,
              landingAppointmentModal,
              serverRequestContext,
            ),
        ],
      ),
    ),
    FooterAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedFooterSection;
